/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  productReturnService,
  type GetReturnsParams,
  type ProductReturnRecord,
} from "@src/@api-services/return";

import { SortOrder } from "@src/shared/constants/sort-order.constants";
import type { PaginatedListResponse } from "@src/shared/types/pagination.types";
import type { ProductReturn } from "@src/views/apps/financial/types";

interface ReturnsState {
  data: ProductReturn[];
  total: number;
  params: GetReturnsParams;
  selectedReturn: ProductReturn | null;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
}

const mapReturn = (row: ProductReturnRecord): ProductReturn => ({
  id: String(row.id),
  orderId: String(row.transactionId || row.orderId || row.id),
  transactionId: row.transactionId ? String(row.transactionId) : undefined,
  transactionItemId: row.transactionItemId
    ? String(row.transactionItemId)
    : undefined,
  productName: row.productName ?? row.item?.name ?? null,
  businessName: row.businessName ?? null,
  customerName: row.customerName ?? null,
  customerEmail: row.customerEmail ?? undefined,
  reason: row.reason ?? row.customReason ?? null,
  status: row.status ?? null,
  refundAmount: Number(row.refundAmount ?? row.item?.totalAmount ?? 0),
  currency: String(row.currency ?? ""),
  createdAt: row.createdAt || new Date().toISOString(),
  updatedAt: row.updatedAt ?? undefined,
  inspectionNote: row.inspectionNote,
  inspectionStatus: row.inspectionStatus,
  images: Array.isArray(row.images) ? row.images : [],
  item: row.item
    ? {
        name: row.item.name ?? null,
        quantity: Number(row.item.quantity ?? 0),
        price: Number(row.item.price ?? 0),
        totalAmount: Number(row.item.totalAmount ?? 0),
        itemType: row.item.itemType ?? null,
      }
    : undefined,
});

export const getReturns = createAsyncThunk(
  "returns/getReturns",
  async (params: GetReturnsParams, { rejectWithValue }) => {
    try {
      const response = await productReturnService.getReturns(params);
      const payload = response.data?.data as PaginatedListResponse<ProductReturnRecord>;
      const rows = payload?.data ?? [];

      return {
        params,
        data: rows.map(mapReturn),
        total: payload?.totalRecord ?? 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch returns"
      );
    }
  }
);

export const getReturn = createAsyncThunk(
  "returns/getReturn",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await productReturnService.getReturn(id);
      const data = response.data?.data as ProductReturnRecord | undefined;

      return data?.id ? mapReturn(data) : null;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch return"
      );
    }
  }
);

const initialState: ReturnsState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: SortOrder.DESC,
  },
  selectedReturn: null,
  loading: false,
  detailLoading: false,
  error: null,
};

export const returnsSlice = createSlice({
  name: "returns",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedReturn: (state) => {
      state.selectedReturn = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReturns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReturns.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getReturns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getReturn.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getReturn.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedReturn = action.payload;
      })
      .addCase(getReturn.rejected, (state) => {
        state.detailLoading = false;
        state.selectedReturn = null;
      });
  },
});

export const { clearError, clearSelectedReturn } = returnsSlice.actions;

export default returnsSlice.reducer;
